import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import type { TaxonRank } from "~/api/types";
import { useAuth } from "~/hooks/useAuth";

export type TaxonSearchResult = {
  id: string;
  rank: TaxonRank;
  name: string;
  scientificName: string;
  authorship: string | null;
  commonName: string | null;
  familyName: string | null;
  genusName: string | null;
  verified: boolean;
};

export type TaxonLineageEntry = {
  id: string;
  rank: TaxonRank;
  name: string;
  scientificName: string;
};

/** Ranks a user can realistically create when cataloguing something new. */
export const CONTRIBUTABLE_RANKS: TaxonRank[] = [
  "family",
  "genus",
  "species",
  "subspecies",
  "variety",
  "form",
  "hybrid",
  "cultivar",
];

export const RANK_LABELS: Record<TaxonRank, string> = {
  kingdom: "Kingdom",
  subkingdom: "Subkingdom",
  division: "Division",
  subdivision: "Subdivision",
  class: "Class",
  subclass: "Subclass",
  superorder: "Superorder",
  order: "Order",
  suborder: "Suborder",
  family: "Family",
  subfamily: "Subfamily",
  tribe: "Tribe",
  subtribe: "Subtribe",
  genus: "Genus",
  subgenus: "Subgenus",
  section: "Section",
  series: "Series",
  species: "Species",
  subspecies: "Subspecies",
  variety: "Variety",
  subvariety: "Subvariety",
  form: "Form",
  subform: "Subform",
  hybrid: "Hybrid",
  grex: "Grex",
  cultivar_group: "Cultivar group",
  cultivar: "Cultivar",
};

export function useTaxonSearch(term: string, rankFilter?: TaxonRank[]) {
  const query = term.trim();
  return useQuery({
    queryKey: ["florafolio-taxa", query, rankFilter ?? null],
    // Two characters is the point where results stop being the whole database.
    enabled: query.length >= 2,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<TaxonSearchResult[]> => {
      const { items } = await unwrap(
        api.searchTaxa({
          query: {
            q: query,
            limit: 25,
            ranks: rankFilter?.length ? rankFilter.join(",") : undefined,
          },
        }),
      );
      return items.map((row) => ({
        id: row.id,
        rank: row.rank,
        name: row.name,
        scientificName: row.scientific_name,
        authorship: row.authorship,
        commonName: row.common_name,
        familyName: row.family_name,
        genusName: row.genus_name,
        verified: row.verified,
      }));
    },
  });
}

export function useTaxonLineage(taxonId: string | null | undefined) {
  return useQuery({
    queryKey: ["florafolio-taxon-lineage", taxonId ?? ""],
    enabled: Boolean(taxonId),
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<TaxonLineageEntry[]> => {
      const { items } = await unwrap(
        api.getTaxonLineage({ path: { id: taxonId as string } }),
      );
      return items.map((row) => ({
        id: row.id,
        rank: row.rank,
        name: row.name,
        scientificName: row.scientific_name,
      }));
    },
  });
}

export type ContributeTaxonInput = {
  rank: TaxonRank;
  name: string;
  parentId: string | null;
  authorship?: string;
  commonName?: string;
  nativeRange?: string;
  description?: string;
};

/**
 * User-submitted botanical records become part of the shared public database,
 * flagged unverified until the stage-3 import service reconciles them against
 * an authority.
 */
export function useContributeTaxon() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ContributeTaxonInput) => {
      if (!user) throw new Error("You must be signed in to add a plant name.");

      return unwrap(
        api.contributeTaxon({
          body: {
            rank: input.rank,
            name: input.name.trim(),
            parent_id: input.parentId,
            authorship: input.authorship?.trim() || undefined,
            native_range: input.nativeRange?.trim() || undefined,
            description: input.description?.trim() || undefined,
            common_name: input.commonName?.trim() || undefined,
          },
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["florafolio-taxa"] });
    },
  });
}
